/**
 * Turn a widget spec into plain language for the ⓘ tooltip.
 *
 * Custom widgets are opaque in a way built-in metrics are not — the number came
 * from an MCP tool call plus a transformer the user never sees. This translates
 * the spec (tool, facet, aggregate, formula, display rules, filter scope) into
 * something a merchant can read, so "277" is explainable without opening JSON.
 */
import {
  DashboardWidget,
  ExpressionInput,
  FILTER_AWARE_WIDGET_TOOLS,
  canonicalFacetLabel,
  WidgetDisplay,
  WidgetExtract,
  WidgetFormat,
  WidgetMcpSource,
  formatWidgetValue,
  widgetFilterScope,
} from 'modules/widgetSpec'
import { formatters } from 'modules/formatting'

export interface WidgetExplanation {
  /** One sentence: what this widget shows */
  summary: string
  /** Supporting lines: data source, filter scope, conditional labels */
  details: string[]
  /** The natural-language request that produced the widget, if AI-generated */
  prompt?: string
}

const titleCase = (value: string) =>
  String(value || '')
    .replace(/[._]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase())

const statTitle = (key: string): string | null =>
  ((formatters as any)?.stats?.[key]?.title as string) || null

const facetTitle = (key: string): string | null =>
  ((formatters as any)?.facets?.[key]?.title as string) || null

/** Human label for a grouping dimension (facet key or row label field) */
const dimensionLabel = (key?: string): string =>
  !key ? 'category' : (facetTitle(key) || titleCase(key)).toLowerCase()

/**
 * Human label for a measure. Facet buckets carry `count` (order count) rather
 * than the `total_orders` field the stats API uses — spell that out so the
 * tooltip never reads "count".
 */
const MEASURE_LABELS: Record<string, string> = {
  count: 'order count',
  total_orders: 'order count',
  orders: 'order count',
  value: 'value',
}

const measureLabel = (key?: string): string => {
  if (!key) return 'value'
  if (MEASURE_LABELS[key]) return MEASURE_LABELS[key]
  return (statTitle(key) || titleCase(key)).toLowerCase()
}

/**
 * Short unit for the number on a metric card ("items sold", "order count"). A card
 * whose headline is a NAME shows its number bare underneath, which reads as ambiguous
 * beside the filter sidebar's order counts — "Michael Chen / 668" next to
 * "Michael Chen (220)" looks stale when it is simply a different measure.
 */
export const widgetMeasureLabel = (widget: DashboardWidget): string | null => {
  const source: any = widget.source
  if (source?.type !== 'mcp_tool') return null
  const field = source.extract?.valueField
  if (!field) return null
  // measureLabel already lowercases; drop a leading "total" so it reads as a unit
  const label = measureLabel(field).replace(/^total\s+/, '').trim()
  return label || null
}

/** Friendly names for the tools a widget is allowed to call. */
const TOOL_LABELS: Record<string, string> = {
  get_cross_platform_ads_performance: 'cross-platform ad performance',
  search_google_ads: 'Google Ads',
  search_facebook_ads: 'Meta (Facebook) Ads',
  search_amazon_ads: 'Amazon Ads',
  search_tiktok: 'TikTok Ads',
  search_pinterest: 'Pinterest Ads',
  search_klaviyo: 'Klaviyo',
  search_stripe: 'Stripe',
  get_website_traffic_data: 'website traffic',
  get_website_traffic_metrics: 'website traffic metrics',
  get_website_traffic_summary: 'the website traffic summary',
  get_orders_report: 'the Orders report',
  get_line_items_report: 'the Line Items report',
  get_charts_report: 'the Charts report',
  get_trends_report: 'the Trends report',
  get_year_comparison_report: 'the Year Comparison report',
  get_customers_report: 'the Customers report',
  get_products_report: 'the Products report',
  get_product_trends_report: 'the Product Trends report',
  get_variants_report: 'the Variants report',
  get_brands_report: 'the Brands report',
  get_skus_report: 'the SKUs report',
  get_custom_order_bases_report: 'the Custom Order Bases report',
  get_marketing_report: 'the Marketing report',
  get_marketing_mix_model_summary: 'the marketing mix model',
  get_marketing_mix_channel_contributions: 'marketing mix channel contributions',
  get_marketing_budget_scenarios: 'marketing budget scenarios',
  run_google_analytics_report: 'Google Analytics',
  get_inventory_summary: 'the Inventory summary',
  get_inventory_products: 'Inventory products',
  get_inventory_variants: 'Inventory variants',
  get_inventory_brands: 'Inventory brands',
  gmc_get_performance_report: 'Google Merchant Center',
}

const toolLabel = (tool: string): string => TOOL_LABELS[tool] || `\`${tool}\``

/**
 * Why the dashboard filters can't reach this widget's data. Naming the source is the
 * actionable part: "Google Ads has no view of your orders" tells the merchant the
 * filter is meaningless there, rather than implying the widget is misconfigured.
 */
const unfilteredReason = (widget: DashboardWidget): string => {
  const source: any = widget.source
  if (source?.type === 'report') return 'a saved report brings its own filters'
  if (source?.type === 'mcp_tool') return `${toolLabel(source.tool)} data sits outside your PayHelm orders`
  return 'its data sits outside your PayHelm orders'
}

/** Which side of a mixed calculation moves with the filters, named input by input. */
const partialFilterExplanation = (widget: DashboardWidget): string => {
  const source: any = widget.source
  const inputs: ExpressionInput[] = source?.type === 'expression' ? source.inputs || [] : []
  const label = (input: ExpressionInput) => input.label || input.key
  const follows = inputs.filter(
    input =>
      input.source.type === 'builtin_stat' ||
      (input.source.type === 'mcp_tool' && FILTER_AWARE_WIDGET_TOOLS.has(input.source.tool))
  )
  const doesNot = inputs.filter(
    input => input.source.type === 'mcp_tool' && !FILTER_AWARE_WIDGET_TOOLS.has(input.source.tool)
  )
  return `Filtered: ${follows.map(label).join(', ') || 'none'} · Not filtered: ${
    doesNot.map(label).join(', ') || 'none'
  }`
}

/** Tools whose rows are inherently a time series, so an unstated grouping is time */
const IMPLIED_DIMENSIONS: Record<string, string> = {
  get_trends_report: 'date',
  get_product_trends_report: 'date',
  get_year_comparison_report: 'period',
  get_website_traffic_data: 'date',
  get_website_traffic_metrics: 'date',
}

/** The facet/grouping key a metric or chart is reading (facets param wins over path) */
const groupingKey = (source: WidgetMcpSource): string | undefined => {
  const facets = source.params?.facets
  if (Array.isArray(facets) && facets.length) return String(facets[0])
  const extract = source.extract || {}
  return extract.path || extract.labelField
}

const sentenceCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)

/** "Total sales", "The channel with the highest order count", … */
const aggregatePhrase = (extract: WidgetExtract, dimension: string): string => {
  const measure = measureLabel(extract.valueField)
  // Field names already carry their own aggregate word ("total_sales", "avg_order")
  // — don't stack a second one on top ("Total total sales").
  const selfDescribing = /^(total|avg|average|max|maximum|min|minimum|net|gross)\b/i.test(measure)
  switch (extract.aggregate) {
    case 'avg':
      return selfDescribing ? sentenceCase(measure) : `Average ${measure}`
    case 'max':
      return `Highest ${measure}`
    case 'min':
      return `Lowest ${measure}`
    case 'count':
      return `Number of ${dimension} values`
    case 'last':
      return `Most recent ${measure}`
    case 'argmax':
      return `The ${dimension} with the highest ${measure}`
    case 'argmin':
      return `The ${dimension} with the lowest ${measure}`
    case 'sum':
    default:
      return selfDescribing ? sentenceCase(measure) : `Total ${measure}`
  }
}

const comparatorPhrase = (
  rule: { gte?: number; gt?: number; lte?: number; lt?: number; eq?: number },
  format?: WidgetFormat
): string => {
  // Thresholds read in the metric's own units ($10,000, not 10000) — but whole
  // numbers stay whole regardless of the metric's decimal setting.
  const show = (value: number) =>
    formatWidgetValue(value, { ...format, ...(Number.isInteger(value) ? { decimals: 0 } : {}) })
  const parts: string[] = []
  if (rule.eq !== undefined) parts.push(`= ${show(rule.eq)}`)
  if (rule.gte !== undefined) parts.push(`≥ ${show(rule.gte)}`)
  if (rule.gt !== undefined) parts.push(`> ${show(rule.gt)}`)
  if (rule.lte !== undefined) parts.push(`≤ ${show(rule.lte)}`)
  if (rule.lt !== undefined) parts.push(`< ${show(rule.lt)}`)
  return parts.join(' and ')
}

/** Describe the conditional-label rules ("Shows BINGO when ≥ $10,000") */
const displayDetails = (display: WidgetDisplay, format?: WidgetFormat): string[] => {
  const lines: string[] = []
  const rules = display.rules || []
  rules.slice(0, 3).forEach(rule => {
    if (display.type === 'threshold') {
      const condition = comparatorPhrase(rule as any, format)
      if (condition) lines.push(`Shows “${rule.text}” when the value is ${condition}`)
      else lines.push(`Shows “${rule.text}”`)
    } else {
      const match = rule as any
      const condition =
        match.equals !== undefined
          ? `is “${match.equals}”`
          : Array.isArray(match.in) && match.in.length
            ? `is one of ${match.in.map((m: string) => `“${m}”`).join(', ')}`
            : match.contains !== undefined
              ? `contains “${match.contains}”`
              : ''
      lines.push(condition ? `Shows “${rule.text}” when the result ${condition}` : `Shows “${rule.text}”`)
    }
  })
  if (rules.length > 3) lines.push(`+ ${rules.length - 3} more label rule${rules.length - 3 === 1 ? '' : 's'}`)
  if (display.fallback) lines.push(`Otherwise shows “${display.fallback.text}”`)
  return lines
}

/**
 * One line per formula variable — the key has to appear because the formula
 * references it: "ad_spend (Google spend) = Total cost, from Google Ads".
 */
const expressionInputLine = (input: ExpressionInput): string => {
  const source = input.source
  /** Only show the author's label when it adds something the key doesn't already say */
  const named = (definition: string) => {
    const label = (input.label || '').trim()
    const redundant =
      !label ||
      label.toLowerCase() === input.key.toLowerCase() ||
      label.toLowerCase() === titleCase(input.key).toLowerCase() ||
      definition.toLowerCase().startsWith(label.toLowerCase())
    return `${input.key}${redundant ? '' : ` (${label})`} = ${definition}`
  }
  if (source.type === 'builtin_stat') {
    return named(`${statTitle(source.stat) || titleCase(source.stat)} (dashboard metric)`)
  }
  if (source.type === 'context') {
    return named('days in the selected date range')
  }
  const extract = source.extract || {}
  const key = groupingKey(source)
  const dimension = key ? dimensionLabel(key) : IMPLIED_DIMENSIONS[source.tool] || 'category'
  const scope = extract.labelEquals ? ` for ${extract.labelEquals}` : ''
  return named(`${aggregatePhrase(extract, dimension)}${scope}, from ${toolLabel(source.tool)}`)
}

const hasDatePlaceholders = (params: any): boolean => {
  try {
    return /\{\{\s*(start|end)_date\s*\}\}/.test(JSON.stringify(params || {}))
  } catch (e) {
    return false
  }
}

/**
 * Build the tooltip copy for a widget. Never throws — a malformed spec degrades
 * to the title-only fallback rather than breaking the card.
 */
export const describeWidget = (widget: DashboardWidget): WidgetExplanation => {
  const details: string[] = []
  const source = widget.source
  let summary = ''

  if (source?.type === 'mcp_tool') {
    const extract = source.extract || {}
    const dimension = groupingKey(source)
    // labelFormat names the period a time series is bucketed by ("by week", not "by date")
    const periodFromLabelFormat = extract.labelFormat?.startsWith('week')
      ? 'week'
      : extract.labelFormat === 'month'
        ? 'month'
        : extract.labelFormat === 'day'
          ? 'day'
          : null
    const dimensionText = dimension
      ? dimensionLabel(dimension)
      : periodFromLabelFormat || IMPLIED_DIMENSIONS[source.tool] || 'category'

    if (widget.kind === 'metric' && extract.textField) {
      // Text answers describe the field, not an aggregate ("The store id …")
      summary = `The ${titleCase(extract.textField).toLowerCase()} from ${toolLabel(source.tool)}`
    } else if (widget.kind === 'metric') {
      summary = aggregatePhrase(extract, dimensionText)
      if (extract.labelEquals) summary += ` for ${extract.labelEquals}`
      else if (dimension && extract.aggregate !== 'argmax' && extract.aggregate !== 'argmin') {
        summary += ` across all ${dimensionText} values`
      }
    } else if (widget.kind === 'map') {
      const level =
        widget.map_scope === 'counties' || /county/.test(dimensionText)
          ? 'county'
          : /province|state/.test(dimensionText)
            ? 'state'
            : 'country'
      summary = `${titleCase(measureLabel(extract.valueField))} by ${level}, shaded on a map`
    } else if (widget.kind === 'chart') {
      const chartType = widget.chart_type === 'pie' ? 'share' : widget.chart_type === 'line' ? 'trend' : 'breakdown'
      summary = `${titleCase(measureLabel(extract.valueField))} ${chartType} by ${dimensionText}`
    } else {
      summary = dimension ? `Rows of ${dimensionText} data` : 'Rows returned by the data source'
    }
    if (!(widget.kind === 'metric' && extract.textField)) {
      details.push(`Data source: ${toolLabel(source.tool)}`)
    }
    if (extract.labelEquals) {
      const field = extract.labelField ? titleCase(extract.labelField).toLowerCase() : dimensionText
      // Show the bucket it actually resolves to: a spec saying "false" reads the
      // "Returning" bucket, and "= false" in the tooltip explained nothing.
      details.push(`Limited to ${field} = ${canonicalFacetLabel(extract.labelEquals)}`)
    }
    if (source.params?.filter_config) details.push('Has its own saved filter on top of the dashboard filters')
  } else if (source?.type === 'expression') {
    const inputs = source.inputs || []
    summary = `Calculated: ${source.formula}`
    details.push(...inputs.slice(0, 6).map(expressionInputLine))
    if (inputs.length > 6) details.push(`+ ${inputs.length - 6} more inputs`)
  } else if (source?.type === 'report') {
    summary = 'Data from one of your saved reports'
    details.push('Open report to see the full breakdown')
  } else {
    summary = 'Custom widget'
  }

  if (widget.display) details.push(...displayDetails(widget.display, widget.format))

  // Built-in stats and the range-length context are themselves computed from the
  // selected range, so either one makes the widget date-scoped.
  const usesDates =
    source?.type === 'mcp_tool'
      ? hasDatePlaceholders(source.params)
      : source?.type === 'expression'
        ? (source.inputs || []).some(input =>
            input.source.type === 'mcp_tool'
              ? hasDatePlaceholders((input.source as WidgetMcpSource).params)
              : true
          )
        : false

  // Filter scope, per source — this used to claim "follows the dashboard filters"
  // for every widget that hadn't opted out, which is false for anything sourced
  // outside PayHelm orders: an order filter cannot be sent to Google Ads or Stripe.
  const scope = widgetFilterScope(widget)
  const dateLine = usesDates ? 'Follows the dashboard date range' : ''
  if (scope === 'ignored') {
    details.push(dateLine ? `${dateLine}, but ignores the dashboard filters` : 'Ignores the dashboard filters')
  } else if (scope === 'filtered') {
    details.push(dateLine ? `${dateLine} and filters` : 'Follows the dashboard filters')
  } else if (scope === 'partial') {
    details.push(
      dateLine
        ? `${dateLine}; only part of this calculation follows the filters`
        : 'Only part of this calculation follows the dashboard filters'
    )
    details.push(partialFilterExplanation(widget))
  } else {
    details.push(
      dateLine
        ? `${dateLine}. Dashboard filters do NOT apply — ${unfilteredReason(widget)}`
        : `Dashboard filters do NOT apply — ${unfilteredReason(widget)}`
    )
  }

  return {
    summary,
    details,
    // prompt is only ever set from a natural-language request, so it is safe to
    // show regardless of which surface created the widget
    prompt: widget.prompt || undefined,
  }
}
